import { captureException } from '@sentry/browser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';

import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';

import { isAxiosError } from '../../../api/isAxiosError';
import { deleteWorkspaceFolders } from '../../../api/workspaces/folders/deleteWorkspaceFolders';
import { WorkspaceFolder } from '../../../api/workspaces/folders/types';

const currentFoldersQueryKey = (workspaceId) =>
  workspaceKeys.folders({ workspaceId, isDeleted: false });

const deletedFoldersQueryKey = (workspaceId) =>
  workspaceKeys.folders({ workspaceId, isDeleted: true });

function workspaceFolderUpdater(workspaceFolderIds) {
  return produce((workspaceFolders: WorkspaceFolder[]) =>
    (workspaceFolders ?? []).filter(
      (folder) => !workspaceFolderIds.includes(folder.id)
    )
  );
}

function deletedFoldersUpdate({
  itemsToBeDeleted,
}: {
  itemsToBeDeleted: WorkspaceFolder[];
}) {
  return produce((workspaceFolders: WorkspaceFolder[]) =>
    (workspaceFolders ?? []).concat(itemsToBeDeleted)
  );
}

export function useDeleteWorkspaceFolders() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteWorkspaceFolders,
    onMutate: ({ workspaceId, workspaceFolderIds }) => {
      const currentFolders =
        queryClient.getQueryData<WorkspaceFolder[]>(
          currentFoldersQueryKey(workspaceId)
        ) ?? [];

      const deletedFolders =
        queryClient.getQueryData<WorkspaceFolder[]>(
          deletedFoldersQueryKey(workspaceId)
        ) ?? [];

      queryClient.setQueryData(
        currentFoldersQueryKey(workspaceId),
        workspaceFolderUpdater(workspaceFolderIds)
      );

      const itemsToBeDeleted = currentFolders.filter((folder) =>
        workspaceFolderIds.includes(folder.id)
      );

      queryClient.setQueryData(
        deletedFoldersQueryKey(workspaceId),
        deletedFoldersUpdate({ itemsToBeDeleted: itemsToBeDeleted })
      );

      return { currentFolders, deletedFolders };
    },
    onSettled: (_data, error, { workspaceId }) => {
      return Promise.all([
        queryClient.invalidateQueries(workspaceKeys.allFolders(workspaceId)),
        queryClient.invalidateQueries(workspaceKeys.allQuizzes(workspaceId)),
        queryClient.invalidateQueries(workspaceKeys.list()),
      ]);
    },
    onError: (error, { workspaceId }, { currentFolders, deletedFolders }) => {
      if (!isAxiosError(error)) {
        captureException(error);
      } else if (error.response.status >= 500) {
        captureException(error);
      }

      queryClient.setQueryData(
        currentFoldersQueryKey(workspaceId),
        currentFolders
      );

      queryClient.setQueryData(
        deletedFoldersQueryKey(workspaceId),
        deletedFolders
      );
    },
  });
}
